import { Button } from "@/components/ui/button";
import {
  RENEWAL_EMAIL,
  RENEWAL_PHONE,
  RENEWAL_PHONE_DISPLAY,
  RENEWAL_WHATSAPP,
} from "@/components/subscription/RenewSubscriptionDialog";
import { useAuthContext } from "@/hooks/useAuthContext";
import { Building2, Lock, Mail, MessageCircle, Phone, WifiOff } from "lucide-react";

type NoOfficeScreenProps = {
  /** When set, show connector-denied copy instead of «not linked to office». */
  mode?: "no_office" | "connector_denied";
};

const SupportContacts = ({
  emailSubject,
  whatsappMessage,
}: {
  emailSubject: string;
  whatsappMessage: string;
}) => (
  <div className="mx-auto grid w-full max-w-sm gap-2 text-start">
    <Button
      type="button"
      variant="outline"
      className="h-auto justify-start gap-3 py-3"
      onClick={() =>
        window.open(
          `https://wa.me/${RENEWAL_WHATSAPP}?text=${encodeURIComponent(whatsappMessage)}`,
          "_blank",
        )
      }
    >
      <MessageCircle className="h-5 w-5 text-green-600" />
      <span className="flex flex-col items-start">
        <span className="font-medium">וואטסאפ</span>
        <span className="text-xs text-muted-foreground" dir="ltr">
          {RENEWAL_PHONE_DISPLAY}
        </span>
      </span>
    </Button>
    <Button
      type="button"
      variant="outline"
      className="h-auto justify-start gap-3 py-3"
      onClick={() => {
        window.location.href = `tel:${RENEWAL_PHONE}`;
      }}
    >
      <Phone className="h-5 w-5 text-blue-600" />
      <span className="flex flex-col items-start">
        <span className="font-medium">טלפון</span>
        <span className="text-xs text-muted-foreground" dir="ltr">
          {RENEWAL_PHONE_DISPLAY}
        </span>
      </span>
    </Button>
    <Button
      type="button"
      variant="outline"
      className="h-auto justify-start gap-3 py-3"
      onClick={() => {
        window.location.href = `mailto:${RENEWAL_EMAIL}?subject=${encodeURIComponent(emailSubject)}`;
      }}
    >
      <Mail className="h-5 w-5 text-amber-600" />
      <span className="flex flex-col items-start">
        <span className="font-medium">אימייל</span>
        <span className="text-xs text-muted-foreground" dir="ltr">
          {RENEWAL_EMAIL}
        </span>
      </span>
    </Button>
  </div>
);

export const NoOfficeScreen = ({ mode = "no_office" }: NoOfficeScreenProps) => {
  const { logout, user, backendReachable, connectorDenied } = useAuthContext();

  // Backend unreachable — connectivity issue, not missing office
  if (!backendReachable) {
    return (
      <div className="flex h-screen items-center justify-center bg-background" dir="rtl">
        <div className="max-w-lg space-y-6 p-8 text-center">
          <div className="mx-auto flex h-16 w-20 items-center justify-center rounded-full bg-red-50">
            <WifiOff className="h-8 w-8 text-red-500" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">שגיאת תקשורת</h1>
            <p className="text-muted-foreground">
              לא ניתן להתחבר לשרת. אנא נסה שוב מאוחר יותר.
            </p>
          </div>

          <div className="space-y-3">
            <Button onClick={() => window.location.reload()}>נסה שוב</Button>
            <div>
              <Button variant="outline" onClick={() => logout()}>
                התנתק
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showConnectorDenied =
    mode === "connector_denied" || connectorDenied != null;

  if (showConnectorDenied) {
    return (
      <div className="flex h-screen items-center justify-center bg-background" dir="rtl">
        <div className="max-w-lg space-y-6 p-8 text-center">
          <div className="mx-auto flex h-16 w-20 items-center justify-center rounded-full bg-indigo-50">
            <Lock className="h-8 w-8 text-indigo-600" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              הערוץ אינו כלול בתוכנית
            </h1>
            <p className="text-muted-foreground">
              התוכנית שלך אינה כוללת את ערוץ ה־Web app (webapp-connector).
              <br />
              פנה לתמיכה או שדרג את התוכנית כדי להשתמש באפליקציית האינטרנט.
            </p>
            {user?.email ? (
              <p className="pt-1 text-sm text-muted-foreground">{user.email}</p>
            ) : null}
          </div>

          <SupportContacts
            emailSubject="בקשה לגישת Web app"
            whatsappMessage="שלום, אין לי גישה ל־Web app ואשמח לעזרה"
          />

          <div>
            <Button variant="outline" onClick={() => logout()}>
              התנתק
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Backend reachable but user has no office
  return (
    <div className="flex h-screen items-center justify-center bg-background" dir="rtl">
      <div className="max-w-lg space-y-6 p-8 text-center">
        <div className="mx-auto flex h-16 w-20 items-center justify-center rounded-full bg-muted">
          <Building2 className="h-8 w-8 text-muted-foreground" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">אינך משוייך למשרד</h1>
          <p className="text-muted-foreground">
            החשבון שלך ({user?.email}) עדיין לא משויך למשרד.
            <br />
            פנה למנהל המערכת כדי לקבל הזמנה.
          </p>
        </div>

        <SupportContacts
          emailSubject="בקשה להצטרפות למשרד"
          whatsappMessage="שלום, החשבון שלי עדיין לא משויך למשרד ואשמח לעזרה"
        />

        <div>
          <Button variant="outline" onClick={() => logout()}>
            התנתק
          </Button>
        </div>
      </div>
    </div>
  );
};
